import React, { useState } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface EventNoticeListPageProps {
  onBack: () => void;
  onItemClick?: (id: string) => void;
}

type EventNoticeItem = {
  id: string;
  type: "event" | "notice";
  title: string;
  date: string;
  description?: string;
  imageUrl?: string;
  content?: string;
  section?: string;
  footnote?: string;
};

// 이벤트 & 공지사항 데이터
const eventNoticeList: EventNoticeItem[] = [
  {
    id: "en-1",
    type: "notice",
    title: "서비스 이용약관 변경 안내 (2026-02-19)",
    date: "2026-02-19",
    description: "변경된 내용을 확인하시어 서비스 이용에 참고 부탁드립니다.",
    imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 250'%3E%3Crect fill='%23D1E7F5' width='400' height='250'/%3E%3Ctext x='50' y='100' font-size='48' font-weight='bold' fill='%231E3A8A'%3E서비스 이용약관%3C/text%3E%3Ctext x='50' y='160' font-size='48' font-weight='bold' fill='%23000'%3E변경 안내%3C/text%3E%3C/svg%3E",
    content: "허비서AI을 이용해 주시는 고객님께\n감사의 인사를 드립니다.\n\n앱 이용약관의 새로움 개정되었습니다.\n아래 변경된 이용을 확인하시어\n서비스 이용에 참고 부탁드립니다.",
    section: "제8조 (허비서AI 서비스의 내용)\n2. 건강관리 서비스",
    footnote: "(자세한 내용은 아이페이지 > 환경설정 > 서비스 약관에서 확인하실 수 있습니다 .)"
  },
  {
    id: "en-2",
    type: "notice",
    title: "새로운 기획과 더는 담신의 기부 소원!",
    date: "2026-02-10",
    description: "새로운 소식을 확인해보세요.",
    content: "새로운 기획으로\n더욱 나은 서비스를 제공해드리겠습니다."
  },
  {
    id: "en-3",
    type: "event",
    title: "[이벤트] 특별 이벤트 진행!",
    date: "2026-02-02",
    description: "특별 할인 이벤트를 놓치지 마세요.",
    content: "특별 할인 이벤트를 진행 중입니다.\n지금 바로 확인해보세요!"
  },
];

export const EventNoticeListPage: React.FC<EventNoticeListPageProps> = ({ onBack, onItemClick }) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const getSelectedItem = () => {
    return eventNoticeList.find(item => item.id === selectedItemId) || null;
  };

  const handleItemClick = (item: EventNoticeItem) => {
    setSelectedItemId(item.id);
    if (onItemClick) {
      onItemClick(item.id);
    }
  };

  const getTypeLabel = (type: "event" | "notice") => {
    return type === "event" ? "이벤트" : "공지";
  };

  const getTypeBadgeClass = (type: "event" | "notice") => {
    return type === "event"
      ? "bg-amber-100 text-amber-700 border border-amber-200"
      : "bg-emerald-100 text-emerald-700 border border-emerald-200";
  };

  // 상세 뷰
  if (selectedItemId) {
    const item = getSelectedItem();
    if (!item) return null;

    return (
      <SimpleModulePage
        title={item.title}
        subtitle=""
        icon="📄"
        onBack={() => setSelectedItemId(null)}
      >
        {/* 배지 & 제목 영역 */}
        <div className="mx-4 mt-3">
          <div className="flex items-start gap-2 mb-2">
            <div
              className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap flex-shrink-0 ${getTypeBadgeClass(
                item.type
              )}`}
            >
              {getTypeLabel(item.type)}
            </div>
          </div>
          <p className="text-[13px] text-slate-600 leading-relaxed">
            {item.description}
          </p>
        </div>

        {/* 배경 이미지 카드 */}
        {item.imageUrl && (
          <SectionCard className="mt-4 p-0 overflow-hidden">
            <img
              src={item.imageUrl}
              alt="이벤트/공지사항 이미지"
              className="w-full h-auto object-cover rounded-2xl"
            />
          </SectionCard>
        )}

        {/* 본문 내용 */}
        {item.content && (
          <SectionCard>
            <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-line">
              {item.content}
            </p>
          </SectionCard>
        )}

        {/* 섹션 강조 박스 */}
        {item.section && (
          <SectionCard>
            <div className="border-2 border-violet-500 rounded-xl px-4 py-3">
              <p className="text-[12px] text-slate-900 leading-relaxed whitespace-pre-line">
                {item.section}
              </p>
            </div>
          </SectionCard>
        )}

        {/* 하단 주석 */}
        {item.footnote && (
          <SectionCard>
            <p className="text-[10px] text-slate-500 leading-relaxed text-center">
              {item.footnote}
            </p>
          </SectionCard>
        )}
        
      </SimpleModulePage>
    );
  }

  // 리스트 뷰
  return (
    <SimpleModulePage
      title="이벤트&공지사항"
      subtitle="(최신 정보)"
      icon="📢"
      onBack={onBack}
    >
      <SectionCard>
        <div className="space-y-0">
          {eventNoticeList.map((item) => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="w-full py-3 px-0 text-left border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex items-start gap-3"
            >
              {/* 배지 */}
              <div
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap flex-shrink-0 mt-0.5 ${getTypeBadgeClass(
                  item.type
                )}`}
              >
                {getTypeLabel(item.type)}
              </div>

              {/* 내용 */}
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-slate-900 mb-1 line-clamp-2">
                  {item.title}
                </p>
                <p className="text-[11px] text-slate-500">
                  {item.date}
                </p>
              </div>
            </button>
          ))}
        </div>
      </SectionCard>
      
    </SimpleModulePage>
  );
};
